import React from "react";
import { cloneDeep } from "lodash";
import { FileSpreadsheet } from "lucide-react";

import { TimeSeries, AlignMethod, DateHelper } from "../../../common/src";
import { exportToXlsx } from "../../../components/src/common/exportToXlsx";
import { TimeSeriesLines } from "../../../components/src/charts/TimeSeriesLines";
import { seriesFromTimeSeries } from "../../../components/src/charts/chartTheme";

const SERIES_COLORS = ["#66BC6B", "#BDBDBD", "#B3E5FC", "#1F5F32", "#757575", "#01579B", "#AB47BC", "#4A148C"];

type RelativeTimeSeriesChartInput = {
    masterTimeSerie: TimeSeries;
    timeseries: TimeSeries[];
    startDate?: string;
    style?: React.CSSProperties | undefined;
    showXlsxButton?: boolean;
};

export const RelativeTimeSeriesChart = ({
    masterTimeSerie,
    timeseries,
    startDate,
    showXlsxButton = false,
    style
}: RelativeTimeSeriesChartInput): React.ReactElement => {
    const diffTimeSeries = [];
    let masterTs = cloneDeep(masterTimeSerie);
    const tsArray = cloneDeep(timeseries);
    if (startDate) {
        masterTs = masterTs.range(new Date(startDate), masterTs.end);
        for (let i = 0; i < tsArray.length; i++) {
            tsArray[i] = TimeSeries.align(masterTs, tsArray[i], AlignMethod.Latest);
        }
    }

    if (tsArray && tsArray.length > 0) {
        for (let i = 0; i < tsArray.length; i++) {
            const ts = tsArray[i];
            if (ts.name !== masterTs.name) {
                const diff = masterTs.normalize(1).subtract(ts.normalize(1));
                diff.name = ts.name;
                diffTimeSeries.push(diff);
            }
        }
    }

    const series = diffTimeSeries.map((ts, index) => {
        const named = seriesFromTimeSeries(ts, SERIES_COLORS[(index + 1) % SERIES_COLORS.length]);
        named.fill = true;
        return named;
    });

    return (
        <div>
            {showXlsxButton ? (
                <div
                    style={{ width: "20px", height: "20px", marginBottom: "0.5rem" }}
                    onClick={() => {
                        const data: Record<string, Record<string, string | number>> = {};
                        series.forEach((item) => {
                            for (const point of item.points) {
                                const date = DateHelper.timestampToString(point.date);
                                if (data[date]) {
                                    data[date][item.name] = point.value;
                                } else {
                                    data[date] = { Date: date, [item.name]: point.value };
                                }
                            }
                        });
                        exportToXlsx([Object.values(data)], "relative_performance.xlsx", ["relative_performance"]);
                    }}
                >
                    <FileSpreadsheet className="Excel" />
                </div>
            ) : null}
            <div className="chart-print" style={style}>
                <TimeSeriesLines
                    series={series}
                    yFormat={(value) => (value * 100).toFixed(1) + "%"}
                    showLegend={false}
                    ariaLabel="Relative performance"
                    height={360}
                    style={{ width: "100%", height: "100%" }}
                />
            </div>
        </div>
    );
};

export default RelativeTimeSeriesChart;
